import { useState, useEffect, useRef, Fragment } from "react";
import compassIcon from '../assets/compass-icon.png';
import FeedbackControl from "./FeedbackControl";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const tealChip = {
  background: 'rgba(45,212,191,.08)',
  border: '1px solid rgba(45,212,191,.25)',
  color: 'var(--accent)',
  fontFamily: 'var(--font-data)',
  fontSize: '10px',
  fontWeight: '600',
  padding: '2px 7px',
  borderRadius: '5px',
  cursor: 'pointer',
};

const premiumPill = {
  fontFamily: 'var(--font-data)',
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--warning)',
  background: 'rgba(224,160,80,.12)',
  border: '1px solid rgba(224,160,80,.28)',
  padding: '1px 5px',
  borderRadius: 4,
};

const errorPill = {
  fontFamily: 'var(--font-data)',
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--negative)',
  background: 'rgba(224,106,90,.08)',
  border: '1px solid rgba(224,106,90,.25)',
  padding: '1px 5px',
  borderRadius: 4,
  cursor: 'help',
};

const TH = {
  fontFamily: 'var(--font-data)',
  color: 'var(--text-4)', fontSize: 10, fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.08em',
  padding: '6px 10px', textAlign: 'left',
  background: 'var(--subbar-bg)',
  borderBottom: '1px solid var(--line)',
};
const TD = {
  padding: '8px 10px', fontSize: 12,
  color: 'var(--text-1)', verticalAlign: 'middle',
};
const ROW_BORDER = { borderBottom: '1px solid var(--line-soft)' };

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// RECENT-FORMERS-1 (D-47). "Left within the last 24 calendar months": in Nov 2026 that is
// anyone who left Nov 2024 or later. eligible_after is stored as departure month + 6 months
// (YYYY-MM-01), so the test is eligible_after >= first of the month 18 months ago. Leads with
// no eligible_after (no departure date known, or still current) are not recent formers.
export const RECENT_FORMER_WINDOW_MONTHS = 24;
export function recentFormerCutoffISO(now = new Date()) {
  const back = RECENT_FORMER_WINDOW_MONTHS - 6;
  let y = now.getUTCFullYear();
  let m = now.getUTCMonth() + 1 - back;
  while (m <= 0) { m += 12; y -= 1; }
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-01`;
}
export function isRecentFormer(lead, cutoffISO) {
  return !!(lead && lead.eligible_after && lead.eligible_after >= cutoffISO);
}
// "Nov 2024" for a Nov 2026 clock: the earliest departure month the filter keeps.
export function recentFormerSinceLabel(now = new Date()) {
  let y = now.getUTCFullYear();
  let m = now.getUTCMonth() + 1 - RECENT_FORMER_WINDOW_MONTHS;
  while (m <= 0) { m += 12; y -= 1; }
  return `${MONTHS_SHORT[m - 1]} ${y}`;
}

function StatusChip({ status, months, eligibleAfter }) {
  const dot = (color) => ({
    display: 'inline-block', width: 7, height: 7,
    borderRadius: '50%', background: color,
    marginRight: 5, flexShrink: 0, verticalAlign: 'middle',
  });
  const chip = (dotColor, label, textColor) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 12, fontWeight: 500, color: textColor }}>
      <span style={dot(dotColor)} />
      {label}
    </span>
  );

  if (!status || status === 'UNKNOWN') {
    return <span style={{ fontSize: 12, color: 'var(--text-5)' }}>—</span>;
  }
  if (status === 'CURRENT') {
    return chip('var(--text-3)', 'Current', 'var(--text-3)');
  }
  if (status === 'DEPARTED') {
    if (eligibleAfter) {
      const today = new Date().toISOString().slice(0, 10);
      const [yr, mo] = eligibleAfter.split('-');
      const monLabel = `${MONTHS_SHORT[parseInt(mo, 10) - 1]} ${yr}`;
      if (eligibleAfter <= today) {
        return chip('var(--accent)', 'Eligible', 'var(--accent)');
      }
      return chip('var(--warning)', `Eligible ${monLabel}`, 'var(--warning)');
    }
    const label = months != null ? `Departed ${Math.round(months)}mo` : 'Departed';
    return chip('var(--warning)', label, 'var(--warning)');
  }
  return <span style={{ fontSize: 12, color: 'var(--text-5)' }}>—</span>;
}

function ScoreCell({ score, size = 13 }) {
  if (score === null || score === undefined) {
    return <span style={{ fontFamily: 'var(--font-data)', fontSize: size, color: 'var(--text-5)' }}>—</span>;
  }
  if (score === 0) {
    return <span style={{ fontFamily: 'var(--font-data)', fontSize: size, color: 'var(--negative)' }}>0</span>;
  }
  const color = score >= 80 ? 'var(--accent)' : score >= 60 ? 'var(--text-2)' : 'var(--text-3)';
  const weight = score >= 80 ? 600 : 400;
  return <span style={{ fontFamily: 'var(--font-data)', fontSize: size, color, fontWeight: weight }}>{score}</span>;
}

// dimmed / highlightId / openRequest are optional (Source flow D): fade the list
// while a new search runs, mark the batch it just created, and open that batch
// (and start ranking it) when the panel's "Rank my leads" is clicked.
export default function SourcingBatches({ project, token, refreshKey, onStats, dimmed = false, highlightId = null, openRequest = null, onRankStatus = null }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [leadsCache, setLeadsCache] = useState({});
  const [leadsLoadingIds, setLeadsLoadingIds] = useState(new Set());
  const [leadsErrors, setLeadsErrors] = useState({});
  // Stale-guard: monotonic seq per batch id; stale responses are discarded.
  const fetchSeqRef  = useRef({});
  // Coalescing: only one in-flight fetch per batch at a time.
  const inFlightRef  = useRef({});
  // Dirty flag: a refresh arrived while a fetch was in flight; fire one follow-up.
  const pendingRef   = useRef({});
  const [runsCache, setRunsCache] = useState({});
  const [rationaleId, setRationaleId] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [scoreMsg, setScoreMsg] = useState(null);
  const [scoreStall, setScoreStall] = useState(null);
  const [scoreError, setScoreError] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [ranking, setRanking] = useState(false);
  const [rankMsg, setRankMsg] = useState(null);
  const [rankStall, setRankStall] = useState(null);
  const [rankError, setRankError] = useState(null);
  const [sortMode, setSortMode] = useState('relevance');
  const [projects, setProjects] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [importProjectId, setImportProjectId] = useState(
    project?.id != null ? String(project.id) : ""
  );
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const [importError, setImportError] = useState(null);
  const [companyFilter, setCompanyFilter] = useState('');
  // RECENT-FORMERS-1: show only formers who left within the last 24 calendar months.
  const [recentFormersOnly, setRecentFormersOnly] = useState(false);
  const [searchDetailsCache, setSearchDetailsCache] = useState({});
  const [expandedTopics, setExpandedTopics] = useState(new Set());
  const [expandedDetails, setExpandedDetails] = useState(new Set());
  // U3: auto-expand the batch card once ranking finishes, unless user toggled it first
  const autoExpandRef = useRef(null);
  const userToggledDuringRankRef = useRef(new Set());

  useEffect(() => {
    fetch(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setProjects)
      .catch(() => {});
  }, []); // eslint-disable-line

  const fetchBatches = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const url = project
        ? `${API}/projects/${project.id}/sourcing-batches`
        : `${API}/sourcing-batches`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setBatches(await res.json());
    } catch (e) {
      setFetchError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async (batchId) => {
    // Coalescing: if a fetch is already in flight for this batch, mark dirty and return.
    // The in-flight fetch will issue one follow-up when it settles.
    if (inFlightRef.current[batchId]) {
      pendingRef.current[batchId] = true;
      return;
    }

    const seq = (fetchSeqRef.current[batchId] = (fetchSeqRef.current[batchId] || 0) + 1);
    inFlightRef.current[batchId] = true;
    setLeadsLoadingIds(prev => new Set([...prev, batchId]));

    try {
      const res = await fetch(`${API}/sourcing-batches/${batchId}?sort=score`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Stale-guard: discard if a newer request has superseded this one.
      if (fetchSeqRef.current[batchId] !== seq) return;
      setLeadsCache(prev => ({ ...prev, [batchId]: data.leads }));
      setSearchDetailsCache(prev => ({ ...prev, [batchId]: data.search_details || null }));
      setLeadsErrors(prev => { const n = { ...prev }; delete n[batchId]; return n; });
    } catch (e) {
      if (fetchSeqRef.current[batchId] !== seq) return;
      // Write error state only — never write [] to the cache on failure.
      setLeadsErrors(prev => ({ ...prev, [batchId]: e.message }));
    } finally {
      // Only the latest sequence clears the in-flight flag and handles pending.
      if (fetchSeqRef.current[batchId] === seq) {
        inFlightRef.current[batchId] = false;
        setLeadsLoadingIds(prev => { const n = new Set(prev); n.delete(batchId); return n; });
        if (pendingRef.current[batchId]) {
          delete pendingRef.current[batchId];
          fetchLeads(batchId);
        }
      }
    }
  };

  const fetchRuns = async (batchId) => {
    try {
      const res = await fetch(`${API}/sourcing-batches/${batchId}/apify-runs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setRunsCache(prev => ({ ...prev, [batchId]: data }));
    } catch (e) {}
  };

  // Fetch batch list on mount and refreshKey change
  useEffect(() => {
    fetchBatches();
  }, [project?.id, refreshKey]); // eslint-disable-line

  useEffect(() => {
    if (!onStats) return;
    const totalLeads = batches.reduce((s, b) => s + (b.total_leads || 0), 0);
    const totalKnown = batches.reduce((s, b) => s + (b.known_expert_matches || 0), 0);
    onStats({ batches: batches.length, leads: totalLeads, known: totalKnown });
  }, [batches]); // eslint-disable-line

  // Re-fetch leads for the expanded batch on refreshKey change.
  // Coalescing handles refresh storms: concurrent calls collapse to one trailing fetch.
  // Cache is NOT cleared — existing leads remain visible while the refresh is in flight.
  useEffect(() => {
    if (expandedId) {
      fetchLeads(expandedId);
      fetchRuns(expandedId);
    }
  }, [refreshKey]); // eslint-disable-line

  // Source flow D: open + scroll to a batch on request from the New search panel,
  // and start ranking it when asked (same call as the batch's own Rank relevance
  // button). Skipped when the batch has no topic or a rank is already running.
  // noExpand: start ranking without opening the card (card opens when ranking finishes).
  useEffect(() => {
    if (!openRequest || openRequest.id == null) return;
    if (openRequest.noExpand) {
      autoExpandRef.current = openRequest.id;
      userToggledDuringRankRef.current.delete(openRequest.id);
    } else {
      if (expandedId !== openRequest.id) toggleBatch(openRequest.id);
    }
    const el = document.getElementById(`sourcing-batch-${openRequest.id}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const target = batches.find(b => b.id === openRequest.id);
    if (openRequest.rank && target && (target.search_context || '').trim() && !ranking) {
      handleRank(openRequest.id);
    }
  }, [openRequest]); // eslint-disable-line

  const resetActionState = () => {
    setScoring(false);
    setScoreMsg(null);
    setScoreStall(null);
    setScoreError(null);
    setResetting(false);
    setRanking(false);
    setRankMsg(null);
    setRankStall(null);
    setRankError(null);
    setSortMode('relevance');
    setSelectedLeads(new Set());
    setImportMsg(null);
    setImportError(null);
    setImportProjectId(project?.id != null ? String(project.id) : "");
    setCompanyFilter('');
  };

  const toggleBatch = (batchId) => {
    userToggledDuringRankRef.current.add(batchId);
    if (expandedId === batchId) {
      setExpandedId(null);
      setRationaleId(null);
      resetActionState();
      return;
    }
    setExpandedId(batchId);
    setRationaleId(null);
    resetActionState();
    if (!leadsCache[batchId] && !inFlightRef.current[batchId]) fetchLeads(batchId);
    fetchRuns(batchId);
  };

  const handleScore = async (batchId) => {
    setScoring(true);
    setScoreMsg(null);
    setScoreStall(null);
    setScoreError(null);
    let prevRemaining = Infinity;
    try {
      while (true) {
        const res = await fetch(`${API}/sourcing-batches/${batchId}/score`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ limit: 40 }),
        });
        if (res.status === 409) {
          setScoreMsg('Auto-score in progress — results will appear shortly.');
          break;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const remaining = data.remaining;
        if (remaining === 0) {
          setScoreMsg('Scored.');
          await fetchLeads(batchId);
          break;
        }
        if (data.aborted) {
          setScoreStall(data.last_error
            ? `${remaining} leads could not be scored — ${data.last_error}`
            : `${remaining} leads could not be scored — retry later.`);
          setScoreMsg(null);
          break;
        }
        if (remaining >= prevRemaining) {
          setScoreStall(data.last_error
            ? `${remaining} leads could not be scored — ${data.last_error}`
            : `${remaining} leads could not be scored — retry later.`);
          setScoreMsg(null);
          break;
        }
        prevRemaining = remaining;
        setScoreMsg(`Scoring… ${remaining} remaining`);
      }
    } catch (e) {
      setScoreError(e.message);
    } finally {
      setScoring(false);
    }
  };

  const handleReset = async (batchId) => {
    if (!window.confirm('Reset all scores for this batch? Score history is preserved, but current scores will be cleared.')) return;
    setResetting(true);
    setScoreMsg(null);
    setScoreStall(null);
    setScoreError(null);
    try {
      const res = await fetch(`${API}/sourcing-batches/${batchId}/reset-scores`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchLeads(batchId);
    } catch (e) {
      setScoreError(e.message);
    } finally {
      setResetting(false);
    }
  };

  const handleRank = async (batchId) => {
    setRanking(true);
    setRankMsg(null);
    setRankStall(null);
    setRankError(null);
    let prevRemaining = Infinity;
    let total = null;
    try {
      while (true) {
        const res = await fetch(`${API}/sourcing-batches/${batchId}/relevance`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ limit: 25 }),
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(res.status === 409 ? (errData.error || 'Already running') : `HTTP ${res.status}`);
        }
        const data = await res.json();
        const remaining = data.remaining;
        if (total === null) total = remaining;
        if (remaining === 0) {
          setRankMsg('Ranked.');
          if (onRankStatus) onRankStatus({ batchId, state: 'done', msg: null });
          await fetchLeads(batchId);
          if (autoExpandRef.current === batchId && !userToggledDuringRankRef.current.has(batchId)) {
            setExpandedId(batchId);
            setRationaleId(null);
            fetchRuns(batchId);
            autoExpandRef.current = null;
          }
          break;
        }
        if (data.aborted) {
          const errMsg = data.last_error
            ? `${remaining} leads could not be ranked — ${data.last_error}`
            : `${remaining} leads could not be ranked — retry later.`;
          setRankStall(errMsg);
          setRankMsg(null);
          if (onRankStatus) onRankStatus({ batchId, state: 'error', msg: errMsg });
          break;
        }
        if (remaining >= prevRemaining) {
          const errMsg = data.last_error
            ? `${remaining} leads could not be ranked — ${data.last_error}`
            : `${remaining} leads could not be ranked — retry later.`;
          setRankStall(errMsg);
          setRankMsg(null);
          if (onRankStatus) onRankStatus({ batchId, state: 'error', msg: errMsg });
          break;
        }
        prevRemaining = remaining;
        const ranked = total !== null ? total - remaining : 0;
        setRankMsg(`Ranking… ${remaining} remaining`);
        if (onRankStatus) onRankStatus({ batchId, state: 'ranking', msg: `Ranking your leads… ${ranked} of ${total}` });
      }
    } catch (e) {
      setRankError(e.message);
      if (onRankStatus) onRankStatus({ batchId, state: 'error', msg: e.message });
    } finally {
      setRanking(false);
    }
  };

  const handleResetRelevance = async (batchId) => {
    if (!window.confirm('Clear relevance rankings and re-run? History is kept.')) return;
    setRankMsg(null);
    setRankStall(null);
    setRankError(null);
    try {
      const res = await fetch(`${API}/sourcing-batches/${batchId}/reset-relevance`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(res.status === 409 ? (errData.error || 'Already running') : `HTTP ${res.status}`);
      }
      await fetchLeads(batchId);
      await handleRank(batchId);
    } catch (e) {
      setRankError(e.message);
    }
  };

  const handleImport = async (batchId) => {
    setImporting(true);
    setImportMsg(null);
    setImportError(null);
    try {
      const res = await fetch(`${API}/sourcing-batches/${batchId}/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lead_ids: [...selectedLeads], project_id: Number(importProjectId) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const proj = projects.find(p => p.id === Number(importProjectId));
      setImportMsg(`Imported ${data.imported} into ${proj?.name || `project ${importProjectId}`}.`);
      setSelectedLeads(new Set());
      await fetchLeads(batchId);
    } catch (e) {
      setImportError(e.message);
    } finally {
      setImporting(false);
    }
  };

  const fmtDate = (s) => (s ? s.slice(0, 10) : '');

  const fmtTenure = (months) => {
    if (months == null) return null;
    const n = Math.round(months);
    if (n < 24) return `${n}mo`;
    const y = Math.floor(n / 12);
    const m = n % 12;
    return m === 0 ? `${y}y` : `${y}y ${m}mo`;
  };

  const fmtMonthYear = (iso) => {
    if (!iso) return null;
    const [yr, mo] = iso.split('-');
    return `${MONTHS_SHORT[parseInt(mo, 10) - 1]} ${yr}`;
  };

  const departureFromEligible = (eligibleAfter) => {
    // coupled to the 6-month eligibility rule in the backend (_eligible_after)
    const [yr, mo] = eligibleAfter.split('-').map(Number);
    let depMo = mo - 6;
    let depYr = yr;
    if (depMo <= 0) { depMo += 12; depYr -= 1; }
    return `${MONTHS_SHORT[depMo - 1]} ${depYr}`;
  };

  if (loading) {
    return <div style={{ color: 'var(--text-3)', fontSize: 12, padding: '12px 16px' }}>Loading…</div>;
  }
  if (fetchError) {
    return (
      <div style={{ color: 'var(--negative)', fontSize: 12, padding: '12px 16px' }}>
        Failed to load sourcing batches.{' '}
        <span style={{ cursor: 'pointer', color: 'var(--accent)', textDecoration: 'underline' }} onClick={fetchBatches}>Retry</span>
      </div>
    );
  }
  if (batches.length === 0) {
    return <div style={{ color: 'var(--text-3)', fontSize: 12, padding: '12px 16px' }}>No sourcing batches yet.</div>;
  }

  return (
    <div>
      {batches.map(batch => {
        const isExpanded = expandedId === batch.id;
        const isNew = highlightId != null && batch.id === highlightId;
        const leads = leadsCache[batch.id] || [];
        const batchLoading = leadsLoadingIds.has(batch.id);
        const batchError = leadsErrors[batch.id];
        const unscoredCount = leads.filter(l => l.score === null).length;
        const hasScoredLeads = leads.some(l => l.score !== null);
        const busy = scoring || resetting || importing || ranking;
        const hasContext = !!(batch.search_context || '').trim();
        const unrankedCount = leads.filter(l => l.relevance_score === null).length;
        const hasRelevanceRankings = leads.some(l => l.relevance_score !== null);
        const allRanked = hasRelevanceRankings && unrankedCount === 0;
        const todayUTC = new Date().toISOString().slice(0, 10);
        const isRipening = lead => !!(lead.eligible_after && lead.eligible_after > todayUTC);
        const showScore = !['competitor', 'customer'].includes(batch.angle);
        const effectiveSortMode = showScore ? sortMode : 'relevance';
        const displayLeads = (hasContext && hasRelevanceRankings && effectiveSortMode === 'relevance')
          ? [...leads].sort((a, b) => {
              const aRip = isRipening(a) ? 1 : 0;
              const bRip = isRipening(b) ? 1 : 0;
              if (aRip !== bRip) return aRip - bRip;
              if (a.relevance_score === null && b.relevance_score === null) return (b.score || 0) - (a.score || 0);
              if (a.relevance_score === null) return 1;
              if (b.relevance_score === null) return -1;
              if (b.relevance_score !== a.relevance_score) return b.relevance_score - a.relevance_score;
              return (b.score || 0) - (a.score || 0);
            })
          : leads;
        const colCount = hasContext ? (showScore ? 9 : 8) : (showScore ? 8 : 7);
        const searchDetails = searchDetailsCache[batch.id] || null;
        const batchRuns = runsCache[batch.id] || [];
        const companyOptions = [...new Set(leads.map(l => l.company).filter(Boolean))].sort();
        const isFormerBatch = (batch.angle || 'former') === 'former';
        const recentCutoff = recentFormerCutoffISO();
        const recentFormerCount = isFormerBatch ? leads.filter(l => isRecentFormer(l, recentCutoff)).length : 0;
        const recentActive = isFormerBatch && recentFormersOnly;
        const filteredLeads = displayLeads
          .filter(l => !companyFilter || l.company === companyFilter)
          .filter(l => !recentActive || isRecentFormer(l, recentCutoff));
        // Select-all acts on the rows the user can see (both filters), not the whole batch.
        const selectableLeads = filteredLeads;
        const allSelected = selectableLeads.length > 0 && selectableLeads.every(l => selectedLeads.has(l.id));
        const firstRipeningIdx = filteredLeads.findIndex(isRipening);
        const showDivider = firstRipeningIdx > 0;
        const inFlightRuns = batchRuns.filter(r => r.status === 'submitted' || r.status === 'running' || r.status === 'ingesting').length;
        const ingestedRuns = batchRuns.filter(r => r.status === 'ingested').length;
        const partialRuns = batchRuns.filter(r => r.status === 'partial').length;
        const failedRuns = batchRuns.filter(r => r.status === 'failed').length;
        return (
          <div
            key={batch.id}
            id={`sourcing-batch-${batch.id}`}
            className={`r-card${isNew ? ' nsp-batch-new' : ''}${dimmed && !isNew ? ' nsp-batch-dim' : ''}`}
            style={{
              marginBottom: 14,
              padding: 0,
              transition: 'opacity .4s',
              ...(isExpanded ? { border: '1px solid rgba(45,212,191,.35)' } : {}),
            }}
          >
            {/* Batch header */}
            <div
              onClick={() => toggleBatch(batch.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                height: 46, padding: '0 16px', cursor: 'pointer',
                borderBottom: isExpanded ? '1px solid var(--line-soft)' : 'none',
              }}
            >
              <span style={{
                display: 'inline-block',
                width: 6, height: 6,
                borderRight: '1.5px solid var(--text-3)',
                borderBottom: '1.5px solid var(--text-3)',
                transform: isExpanded ? 'rotate(45deg) translateY(-2px)' : 'rotate(-45deg)',
                transition: 'transform .2s',
                flexShrink: 0,
              }} />
              <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)', flex: isNew ? '0 1 auto' : 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {batch.label || `Batch #${batch.id}`}
              </span>
              {isNew && (
                <span style={{
                  fontFamily: 'var(--font-data)', fontSize: 10, fontWeight: 500, lineHeight: '18px', padding: '0 6px', borderRadius: 4,
                  color: 'var(--accent)', border: '1px solid rgba(45,212,191,.4)', background: 'rgba(45,212,191,.10)', flexShrink: 0, marginRight: 'auto',
                }}>NEW</span>
              )}
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {batch.project_name && (
                  <span style={{
                    fontFamily: 'var(--font-data)',
                    background: 'rgba(127,168,188,.08)', border: '1px solid rgba(127,168,188,.15)',
                    color: 'var(--text-3)', fontSize: 10, lineHeight: '16px',
                    padding: '0 6px', borderRadius: 4, whiteSpace: 'nowrap',
                  }}>
                    {batch.project_name}
                  </span>
                )}
                {(batch.angle || batch.target_company) && (
                  <Fragment>
                    <span style={{
                      fontFamily: 'var(--font-data)',
                      fontSize: 10, lineHeight: '16px', padding: '0 6px', borderRadius: 4, whiteSpace: 'nowrap',
                      ...((batch.angle || 'former') === 'competitor'
                        ? { background: 'rgba(224,160,80,.12)', border: '1px solid rgba(224,160,80,.28)', color: 'var(--warning)' }
                        : (batch.angle || 'former') === 'customer'
                        ? { background: 'rgba(180,140,242,.12)', border: '1px solid rgba(180,140,242,.3)', color: '#b48cf2' }
                        : { background: 'rgba(127,168,188,.08)', border: '1px solid rgba(127,168,188,.15)', color: 'var(--text-3)' }),
                    }}>
                      {batch.angle || 'former'}
                    </span>
                    {batch.target_company && (
                      <span style={{ fontSize: 12, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                        {batch.target_company}
                      </span>
                    )}
                  </Fragment>
                )}
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{batch.source || 'csv'}</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{fmtDate(batch.created_at)}</span>
                <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{batch.total_leads} leads</span>
                {batch.known_expert_matches > 0 ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0 }} />
                    <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--accent)' }}>{batch.known_expert_matches} known</span>
                  </span>
                ) : (
                  <span style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-5)' }}>—</span>
                )}
              </span>
            </div>

            {/* Leads table */}
            {isExpanded && (
              batchLoading && leads.length === 0 ? (
                <div style={{ padding: '12px 16px', color: 'var(--text-3)', fontSize: 12 }}>Loading…</div>
              ) : batchError && leads.length === 0 ? (
                <div style={{ padding: '12px 16px', color: 'var(--negative)', fontSize: 12 }}>
                  Failed to load — {batchError}.{' '}
                  <span
                    style={{ cursor: 'pointer', color: 'var(--accent)', textDecoration: 'underline' }}
                    onClick={() => {
                      setLeadsErrors(prev => { const n = { ...prev }; delete n[batch.id]; return n; });
                      fetchLeads(batch.id);
                    }}
                  >Retry</span>
                </div>
              ) : leads.length === 0 ? (
                <div style={{ padding: '12px 16px', color: 'var(--text-3)', fontSize: 12 }}>No leads staged.</div>
              ) : (
                <>
                  {/* Topic + Runs meta block */}
                  {(hasContext || batchRuns.length > 0) && (
                    <div style={{
                      background: 'var(--subbar-bg)', borderBottom: '1px solid var(--line-soft)',
                      padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: 6,
                    }}>
                      {hasContext && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                          <span className="r-eyebrow" style={{ flexShrink: 0, paddingTop: 1 }}>Topic</span>
                          <div style={{ flex: 1 }}>
                            {(() => {
                              const _txt = batch.search_context;
                              const _long = _txt.length > 240;
                              const _exp = expandedTopics.has(batch.id);
                              return (
                                <>
                                  <span
                                    data-testid="batch-topic-text"
                                    style={{
                                      fontSize: 12, color: 'var(--text-2)',
                                      display: '-webkit-box',
                                      WebkitBoxOrient: 'vertical',
                                      WebkitLineClamp: (_long && !_exp) ? 3 : 'unset',
                                      overflow: (_long && !_exp) ? 'hidden' : 'visible',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {_txt}
                                  </span>
                                  {_long && (
                                    <button
                                      onClick={() => setExpandedTopics(s => {
                                        const n = new Set(s);
                                        n.has(batch.id) ? n.delete(batch.id) : n.add(batch.id);
                                        return n;
                                      })}
                                      style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', padding: '0 0 0 4px', cursor: 'pointer' }}
                                    >
                                      {_exp ? 'Show less' : 'Show more'}
                                    </button>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      )}
                      {batchRuns.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                          <span className="r-eyebrow">Runs</span>
                          {inFlightRuns > 0 && <span style={{ fontSize: 12, color: 'var(--accent)' }}>{inFlightRuns} in-flight</span>}
                          {ingestedRuns > 0 && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{ingestedRuns} ingested</span>}
                          {partialRuns > 0 && <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{partialRuns} partial</span>}
                          {failedRuns > 0 && <span style={{ fontSize: 12, color: 'var(--negative)' }}>{failedRuns} failed</span>}
                          {inFlightRuns > 0 && <span style={{ fontSize: 12, color: 'var(--text-4)', fontStyle: 'italic' }}>— ingestion is automatic</span>}
                        </div>
                      )}
                      {/* Search details toggle */}
                      {(() => {
                        const _sd = searchDetails;
                        const _sdOpen = expandedDetails.has(batch.id);
                        const _toggleSd = () => setExpandedDetails(s => {
                          const n = new Set(s); n.has(batch.id) ? n.delete(batch.id) : n.add(batch.id); return n;
                        });
                        const _sdRuns = (_sd && _sd.runs) || [];
                        const _probeRuns = _sdRuns.filter(r => r.probe);
                        const _pullRuns = _sdRuns.filter(r => !r.probe && !r.parse_error);
                        // Meta-based runs (CHAIN-FETCH-1b)
                        const _uniqueCountRuns = _sdRuns.filter(r => r.meta && r.meta.kind === 'unique_count');
                        const _groupCountRuns = _sdRuns.filter(r => r.meta && r.meta.kind === 'group_count');
                        const _pullMetaRuns = _sdRuns.filter(r => r.meta && r.meta.kind === 'pull');
                        const _hasMetaRuns = _uniqueCountRuns.length > 0 || _pullMetaRuns.length > 0;
                        // Unique companies across all runs
                        const _compSet = {};
                        _sdRuns.forEach(r => {
                          if (r.parse_error) return;
                          (r.companies || []).forEach(co => {
                            if (!_compSet[co]) _compSet[co] = { name: co, probeTotal: null, staged: 0 };
                          });
                        });
                        _probeRuns.forEach(r => {
                          (r.companies || []).forEach(co => {
                            if (_compSet[co] && r.total_elements != null) {
                              _compSet[co].probeTotal = (_compSet[co].probeTotal || 0) + r.total_elements;
                            }
                          });
                        });
                        _pullRuns.forEach(r => {
                          (r.companies || []).forEach(co => {
                            if (_compSet[co]) _compSet[co].staged += (r.staged || 0);
                          });
                        });
                        // Role groups: from signoff or union of run titles
                        const _signoff = _sd && _sd.signoff;
                        const _roleGroups = _signoff && _signoff.role_groups;
                        const _titleUnion = (() => {
                          const seen = new Set(); const out = [];
                          _sdRuns.forEach(r => { if (r.parse_error) return; (r.titles || []).forEach(t => { if (!seen.has(t)) { seen.add(t); out.push(t); } }); });
                          return out;
                        })();
                        // Filters from first non-probe run (or first run)
                        const _anyRun = _pullRuns[0] || _probeRuns[0] || null;
                        const _exHr = _anyRun ? _anyRun.exclude_hr : (_signoff ? _signoff.exclude_hr : null);
                        const _exJr = _anyRun ? _anyRun.exclude_junior : (_signoff ? _signoff.exclude_junior : null);
                        const _totalStaged = _pullRuns.reduce((s, r) => s + (r.staged || 0), 0);
                        const _compEntries = Object.values(_compSet);
                        const _hasData = _compEntries.length > 0 || _sdRuns.length > 0;
                        if (!_hasData && !_signoff) return null;
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <button
                                data-testid="search-details-toggle"
                                onClick={_toggleSd}
                                style={{ fontSize: 11, color: 'var(--text-4)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-data)', textTransform: 'uppercase', letterSpacing: '0.06em' }}
                              >
                                {_sdOpen ? '▾ Search details' : '▸ Search details'}
                              </button>
                            </div>
                            {_sdOpen && (
                              <div data-testid="search-details-panel" style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', flexDirection: 'column', gap: 8, paddingLeft: 12, borderLeft: '2px solid var(--line-soft)' }}>
                                {/* Unique-count summary (CHAIN-FETCH-1b former batches) */}
                                {_hasMetaRuns ? (() => {
                                  const _coveredGroups = new Set(_uniqueCountRuns.flatMap(r => (r.meta && r.meta.groups) || []));
                                  const _foundUnique = _uniqueCountRuns.reduce((s, r) => s + (r.total_elements || 0), 0);
                                  const _foundGroup = _groupCountRuns
                                    .filter(r => !((r.meta && r.meta.groups) || []).every(g => _coveredGroups.has(g)))
                                    .reduce((s, r) => s + (r.total_elements || 0), 0);
                                  const _totalFound = _foundUnique + _foundGroup;
                                  const _metaBroughtIn = (_pullMetaRuns.length > 0 ? _pullMetaRuns : _pullRuns).reduce((s, r) => s + (r.staged || 0), 0);
                                  return (
                                    <div>
                                      <span style={{ color: 'var(--text-4)', fontFamily: 'var(--font-data)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>People</span>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 }}>
                                        <div style={{ display: 'flex', gap: 8 }}>
                                          {_totalFound > 0 && <span data-testid="sd-unique-found" style={{ color: 'var(--text-4)' }}>{_totalFound} found</span>}
                                          {_metaBroughtIn > 0 && <span style={{ color: 'var(--text-4)' }}>{_metaBroughtIn} brought in</span>}
                                        </div>
                                        <div data-testid="overlap-note" style={{ color: 'var(--text-4)', fontSize: 11, marginTop: 2 }}>
                                          (unique across groups; overlap excluded at search time)
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })() : (
                                  /* Companies — existing behaviour for non-meta batches */
                                  _compEntries.length > 0 && (
                                    <div>
                                      <span style={{ color: 'var(--text-4)', fontFamily: 'var(--font-data)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Companies</span>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 }}>
                                        {_compEntries.map((co, ci) => {
                                          const _coRef = co.name;
                                          const _isUrl = /^https?:\/\//i.test(_coRef);
                                          let _displayName, _displayUrl;
                                          if (_isUrl) {
                                            _displayUrl = _coRef;
                                            const _normRef = _coRef.toLowerCase().replace(/[?#].*$/, '').replace(/\/+$/, '');
                                            const _match = _signoff && (_signoff.companies || []).find(c => {
                                              if (!c.url) return false;
                                              return c.url.toLowerCase().replace(/[?#].*$/, '').replace(/\/+$/, '') === _normRef;
                                            });
                                            if (_match) {
                                              _displayName = _match.name;
                                            } else {
                                              const _slugM = _coRef.match(/linkedin\.com\/company\/([^/?#]+)/i);
                                              _displayName = _slugM ? _slugM[1] : _coRef;
                                            }
                                          } else {
                                            _displayName = _coRef;
                                            _displayUrl = null;
                                          }
                                          return (
                                            <div key={ci} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                              {_displayUrl
                                                ? <a href={_displayUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{_displayName}</a>
                                                : <span>{_displayName}</span>
                                              }
                                              {co.probeTotal != null && <span style={{ color: 'var(--text-4)' }}>{co.probeTotal} found</span>}
                                              {co.staged > 0 && <span style={{ color: 'var(--text-4)' }}>{co.staged} brought in</span>}
                                            </div>
                                          );
                                        })}
                                        {_roleGroups && _roleGroups.length > 1 && (
                                          <div data-testid="overlap-note" style={{ color: 'var(--text-4)', fontSize: 11, marginTop: 2 }}>
                                            (one person can match more than one role group)
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                                {/* Role groups */}
                                <div>
                                  {_roleGroups ? (
                                    <>
                                      <span style={{ color: 'var(--text-4)', fontFamily: 'var(--font-data)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Role groups</span>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 3 }}>
                                        {_roleGroups.map((grp, gi) => (
                                          <div key={gi} data-testid="sd-role-group">
                                            {gi + 1}. {Array.isArray(grp) ? grp.join(', ') : grp}
                                          </div>
                                        ))}
                                      </div>
                                    </>
                                  ) : _titleUnion.length > 0 ? (
                                    <>
                                      <span style={{ color: 'var(--text-4)', fontFamily: 'var(--font-data)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Titles searched</span>
                                      <div style={{ marginTop: 3 }} data-testid="sd-titles-union">{_titleUnion.join(', ')}</div>
                                      <div style={{ color: 'var(--text-4)', fontSize: 11, marginTop: 2 }} data-testid="sd-no-groups-note">role groups weren't saved for this older batch</div>
                                    </>
                                  ) : null}
                                </div>
                                {/* Filters */}
                                {(_exHr != null || _exJr != null) && (
                                  <div>
                                    <span style={{ color: 'var(--text-4)', fontFamily: 'var(--font-data)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Filters</span>
                                    <div style={{ marginTop: 3 }}>
                                      {_exHr != null && <span style={{ marginRight: 12 }}>Exclude HR: {_exHr ? 'yes' : 'no'}</span>}
                                      {_exJr != null && <span>Exclude entry-level: {_exJr ? 'yes' : 'no'}</span>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  {/* Toolbar */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 16px',
                    borderBottom: '1px solid var(--line-soft)',
                    flexWrap: 'wrap',
                  }}>
                    {companyOptions.length > 1 && (
                      <select
                        value={companyFilter}
                        onChange={e => setCompanyFilter(e.target.value)}
                        style={{
                          height: 28, width: 220, fontSize: 12,
                          background: 'var(--input-bg)', border: '1px solid var(--line)',
                          color: companyFilter ? 'var(--text-1)' : 'var(--text-3)',
                          borderRadius: 8, colorScheme: 'dark', padding: '0 8px',
                        }}
                      >
                        <option value="">All companies ({leads.length})</option>
                        {companyOptions.map(c => (
                          <option key={c} value={c}>{c} ({leads.filter(l => l.company === c).length})</option>
                        ))}
                      </select>
                    )}
                    {unscoredCount > 0 && !['customer','partner','competitor'].includes(batch.angle) && (
                      <button
                        className="r-btn r-btn-primary"
                        disabled={busy || batch.scoring_state === 'running'}
                        onClick={() => handleScore(batch.id)}
                        style={{ height: 28, fontSize: 12 }}
                      >
                        {(scoring || batch.scoring_state === 'running') ? 'Scoring…' : `Score ${unscoredCount} leads`}
                      </button>
                    )}
                    {hasContext && !allRanked && unrankedCount > 0 && (
                      <button
                        className="r-btn r-btn-primary"
                        disabled={busy}
                        onClick={() => handleRank(batch.id)}
                        style={{ height: 28, fontSize: 12 }}
                      >
                        {ranking ? 'Ranking…' : `Rank relevance (${unrankedCount})`}
                      </button>
                    )}
                    {hasContext && allRanked && (
                      <button
                        className="r-btn r-btn-ghost"
                        disabled={busy}
                        onClick={() => handleResetRelevance(batch.id)}
                        style={{ height: 28, fontSize: 12 }}
                      >
                        Re-rank
                      </button>
                    )}
                    {isFormerBatch && leads.length > 0 && (
                      <button
                        className={recentActive ? 'r-btn r-btn-primary' : 'r-btn r-btn-ghost'}
                        data-testid="recent-formers-toggle"
                        aria-pressed={recentActive}
                        onClick={() => setRecentFormersOnly(v => !v)}
                        title={`Only formers who left in the last ${RECENT_FORMER_WINDOW_MONTHS} months (${recentFormerSinceLabel()} or later)`}
                        style={{ height: 28, fontSize: 12 }}
                      >
                        {recentActive ? `Recent formers only (${recentFormerCount})` : `Filter for Recent formers (${recentFormerCount})`}
                      </button>
                    )}
                    {scoreMsg && <span style={{ fontSize: 12, color: 'var(--positive)' }}>{scoreMsg}</span>}
                    {scoreStall && <span style={{ fontSize: 12, color: 'var(--warning)' }}>{scoreStall}</span>}
                    {scoreError && <span style={{ fontSize: 12, color: 'var(--negative)' }}>{scoreError}</span>}
                    {rankMsg && <span style={{ fontSize: 12, color: 'var(--positive)' }}>{rankMsg}</span>}
                    {rankStall && <span style={{ fontSize: 12, color: 'var(--warning)' }}>{rankStall}</span>}
                    {rankError && <span style={{ fontSize: 12, color: 'var(--negative)' }}>{rankError}</span>}
                    {hasContext && hasRelevanceRankings && showScore && (
                      <span className="r-seg">
                        <button
                          className={sortMode === 'relevance' ? 'r-seg-on' : ''}
                          onClick={() => setSortMode('relevance')}
                          style={{ fontSize: 12 }}
                        >Relevance</button>
                        <button
                          className={sortMode === 'score' ? 'r-seg-on' : ''}
                          onClick={() => setSortMode('score')}
                          style={{ fontSize: 12 }}
                        >Score</button>
                      </span>
                    )}
                    <select
                      value={importProjectId}
                      onChange={e => setImportProjectId(e.target.value)}
                      disabled={busy}
                      style={{
                        height: 28, width: 200, fontSize: 12,
                        background: 'var(--input-bg)', border: '1px solid var(--line)',
                        color: importProjectId ? 'var(--text-1)' : 'var(--text-3)',
                        borderRadius: 8, colorScheme: 'dark', padding: '0 8px',
                        marginLeft: 'auto',
                      }}
                    >
                      {!project && <option value="" disabled>Choose project…</option>}
                      {projects.map(p => (
                        <option key={p.id} value={String(p.id)}>{p.name}</option>
                      ))}
                    </select>
                    {(() => {
                      const importDisabled = busy || selectedLeads.size === 0 || !importProjectId;
                      return (
                        <button
                          className={importDisabled ? '' : 'r-btn r-btn-primary'}
                          disabled={importDisabled}
                          onClick={() => handleImport(batch.id)}
                          style={importDisabled ? {
                            height: 28, fontSize: 12, padding: '0 14px', borderRadius: 8,
                            background: 'rgba(255,255,255,.05)', color: 'var(--text-4)',
                            border: '1px solid var(--line)', boxShadow: 'none',
                            cursor: 'not-allowed', whiteSpace: 'nowrap',
                          } : { height: 28, fontSize: 12, whiteSpace: 'nowrap' }}
                        >
                          {importing ? 'Importing…' : `Import ${selectedLeads.size} selected`}
                        </button>
                      );
                    })()}
                    {importMsg && <span style={{ fontSize: 12, color: 'var(--positive)' }}>{importMsg}</span>}
                    {importError && <span style={{ fontSize: 12, color: 'var(--negative)' }}>{importError}</span>}
                    {hasScoredLeads && showScore && (
                      <button
                        disabled={busy}
                        onClick={() => handleReset(batch.id)}
                        style={{
                          background: 'none', border: 'none',
                          cursor: busy ? 'default' : 'pointer',
                          color: 'var(--warning)',
                          fontSize: 12, padding: 0,
                          opacity: busy ? 0.5 : 1,
                        }}
                      >
                        {resetting ? 'Resetting…' : 'Reset scores'}
                      </button>
                    )}
                  </div>
                  {ranking && unrankedCount > 0 ? (
                    <div data-testid="ranking-placeholder" style={{ padding: '12px 16px', fontSize: 13, color: 'var(--text-3)' }}>
                      {rankMsg || 'Ranking…'}
                    </div>
                  ) : <><div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', minWidth: 980, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ height: 32 }}>
                      <th style={{ ...TH, width: 36, padding: '0 8px 0 14px' }}>
                        <input
                          type="checkbox"
                          checked={allSelected}
                          style={{ accentColor: 'var(--accent)' }}
                          onChange={e => {
                            if (e.target.checked) setSelectedLeads(new Set(selectableLeads.map(l => l.id)));
                            else setSelectedLeads(new Set());
                          }}
                        />
                      </th>
                      <th style={{ ...TH, minWidth: 180 }}>Name</th>
                      <th style={TH}>
                        {(batch.angle === 'competitor' || batch.angle === 'customer') ? 'Current title' : 'Title at target'}
                      </th>
                      <th style={{ ...TH, width: 120 }}>Location</th>
                      <th style={{ ...TH, width: 190 }}>Tenure</th>
                      <th style={{ ...TH, width: 100 }}>Status</th>
                      {hasContext && <th style={{ ...TH, width: 72, textAlign: 'right' }}>Relevance</th>}
                      {showScore && <th style={{ ...TH, width: 56, textAlign: 'right' }}>Score</th>}
                      <th style={{ ...TH, width: 64 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentActive && filteredLeads.length === 0 && (
                      <tr><td colSpan={colCount} data-testid="recent-formers-empty" style={{ padding: '12px 16px', color: 'var(--text-3)', fontSize: 12 }}>
                        No formers in this batch left {recentFormerSinceLabel()} or later.
                      </td></tr>
                    )}
                    {filteredLeads.map((lead, idx) => (
                      <Fragment key={lead.id}>
                        {showDivider && idx === firstRipeningIdx && (
                          <tr>
                            <td colSpan={colCount} style={{
                              background: 'var(--subbar-bg)',
                              borderBottom: '1px solid var(--line-soft)',
                              padding: '4px 14px',
                            }}>
                              <span className="r-eyebrow">Ripening — eligible later</span>
                            </td>
                          </tr>
                        )}
                        <tr
                          style={{
                            ...ROW_BORDER, cursor: 'pointer', minHeight: 54,
                            background: idx % 2 === 1 ? 'var(--row-odd)' : 'transparent',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'var(--row-hover)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = idx % 2 === 1 ? 'var(--row-odd)' : 'transparent'; }}
                          onClick={() => setRationaleId(id => id === lead.id ? null : lead.id)}
                        >
                          {/* Checkbox */}
                          <td style={{ ...TD, padding: '8px 8px 8px 14px' }}>
                            <input
                              type="checkbox"
                              checked={selectedLeads.has(lead.id)}
                              style={{ accentColor: 'var(--accent)' }}
                              onChange={e => {
                                const checked = e.target.checked;
                                setSelectedLeads(prev => {
                                  const n = new Set(prev);
                                  if (checked) n.add(lead.id); else n.delete(lead.id);
                                  return n;
                                });
                              }}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          {/* Name */}
                          <td style={{ ...TD, minWidth: 180 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)' }}>{lead.name}</span>
                              {lead.sales_nav_url ? (
                                <button
                                  style={tealChip}
                                  onClick={e => { e.stopPropagation(); window.open(lead.sales_nav_url, '_blank'); }}
                                >
                                  <img src={compassIcon} alt="" style={{ width: 11, height: 11, verticalAlign: 'middle', marginRight: 3 }} />
                                  Sales Nav
                                </button>
                              ) : lead.linkedin_url ? (
                                <button
                                  style={tealChip}
                                  onClick={e => { e.stopPropagation(); window.open(lead.linkedin_url, '_blank'); }}
                                >
                                  LinkedIn
                                </button>
                              ) : null}
                              {lead.matched_expert_id && (
                                <span style={{
                                  fontFamily: 'var(--font-data)',
                                  fontSize: 10, color: 'var(--text-3)',
                                  background: 'rgba(127,168,188,.08)',
                                  border: '1px solid rgba(127,168,188,.15)',
                                  padding: '1px 5px', borderRadius: 4,
                                }}>in graph</span>
                              )}
                              {lead.imported_expert_id && (
                                <span style={{
                                  fontFamily: 'var(--font-data)',
                                  fontSize: 10, color: 'var(--text-3)',
                                  background: 'rgba(127,168,188,.08)',
                                  border: '1px solid rgba(127,168,188,.15)',
                                  padding: '1px 5px', borderRadius: 4,
                                }}>Imported</span>
                              )}
                              {lead.premium === 1 && (
                                <span style={premiumPill}>Premium</span>
                              )}
                            </div>
                            {hasContext && lead.relevance_rationale && (
                              <div
                                title={lead.relevance_rationale}
                                data-testid="rel-reason-snippet"
                                style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 260 }}
                              >
                                {lead.relevance_rationale.length > 110 ? lead.relevance_rationale.slice(0, 110) + '…' : lead.relevance_rationale}
                              </div>
                            )}
                          </td>
                          {/* Title */}
                          <td style={TD}>
                            {lead.title_at_target
                              ? <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)' }}>{lead.title_at_target}</span>
                              : <span style={{ color: 'var(--text-5)' }}>—</span>
                            }
                            {(lead.title || lead.company) && (
                              <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
                                {'now: '}
                                {[lead.title, lead.company].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </td>
                          {/* Location */}
                          <td style={TD}>
                            <span style={{ fontSize: 12, color: 'var(--text-3)' }}>
                              {lead.location || '—'}
                            </span>
                          </td>
                          {/* Tenure */}
                          <td style={TD}>
                            {(() => {
                              const rangeStart = fmtMonthYear(lead.start_at_target);
                              let rangeEnd = null;
                              if (rangeStart) {
                                if (lead.employment_status === 'CURRENT') {
                                  rangeEnd = 'Present';
                                } else if (lead.eligible_after) {
                                  rangeEnd = departureFromEligible(lead.eligible_after);
                                }
                              }
                              if (rangeStart && rangeEnd) {
                                return (
                                  <div>
                                    <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-2)' }}>{rangeStart} – {rangeEnd}</span>
                                    {lead.stint_count >= 2 && lead.tenure_months != null && (
                                      <div style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
                                        {fmtTenure(lead.tenure_months)} total · {lead.stint_count} stints
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return fmtTenure(lead.tenure_months)
                                ? <span style={{ fontFamily: 'var(--font-data)', fontSize: 12, color: 'var(--text-2)' }}>{fmtTenure(lead.tenure_months)}</span>
                                : <span style={{ color: 'var(--text-5)' }}>—</span>;
                            })()}
                          </td>
                          {/* Status */}
                          <td style={TD}>
                            <StatusChip status={lead.employment_status} months={lead.months_since_departure} eligibleAfter={lead.eligible_after} />
                          </td>
                          {/* Relevance */}
                          {hasContext && (
                            <td style={{ ...TD, textAlign: 'right' }}>
                              <ScoreCell score={lead.relevance_score} size={13} />
                            </td>
                          )}
                          {/* Score */}
                          {showScore && (
                          <td style={{ ...TD, textAlign: 'right' }}>
                            {lead.score_error
                              ? <span style={errorPill} title={lead.score_error}>Error</span>
                              : <ScoreCell score={lead.score} size={14} />
                            }
                          </td>
                          )}
                          {/* Feedback */}
                          <td style={{ ...TD, textAlign: 'right', paddingRight: 14 }}>
                            <FeedbackControl
                              lead={lead}
                              token={token}
                              onSaved={(verdict) => setLeadsCache(prev => ({
                                ...prev,
                                [batch.id]: (prev[batch.id] || []).map(l =>
                                  l.id === lead.id ? { ...l, feedback_verdict: verdict } : l
                                ),
                              }))}
                            />
                          </td>
                        </tr>
                        {rationaleId === lead.id && (
                          <tr style={{ ...ROW_BORDER, background: 'var(--subbar-bg)' }}>
                            <td colSpan={colCount} style={{ ...TD, fontSize: 12, color: 'var(--text-2)', padding: '10px 20px' }}>
                              {hasContext && lead.relevance_rationale && (
                                <div style={{ marginBottom: 4 }}>
                                  Fit: {lead.relevance_rationale}
                                </div>
                              )}
                              {lead.score_rationale || 'Not scored yet'}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
                </div>
                <div style={{
                  height: 40, display: 'flex', alignItems: 'center',
                  padding: '0 16px', borderTop: '1px solid var(--line-soft)',
                  fontSize: 12, color: 'var(--text-3)',
                }}>
                  {filteredLeads.length} of {leads.length} leads · sorted by {effectiveSortMode === 'relevance' && hasContext && hasRelevanceRankings ? 'relevance' : 'score'}
                </div>
                </>}
                </>
              )
            )}
          </div>
        );
      })}
    </div>
  );
}
